import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import type { Operacion, SolicitudCotizacion } from '@laser/core';
import { usePrograma, useTrabajo } from '../api/hooks';
import { Cargando, Chip, ErrorCaja, Insignia } from '../componentes/ui';
import { fechaHora, relativoDias } from '../lib/fechas';
import { pesos } from '../lib/formato';
import { puedeRecotizar } from '../lib/estados';
import { atrasoDe, totalACobrar } from '../lib/trabajo';
import { Archivos } from './detalle/Archivos';
import { Cotizaciones } from './detalle/Cotizaciones';
import { Gestion, Notas } from './detalle/Gestion';
import { Contacto, Eventos } from './detalle/Laterales';
import { Recotizar } from './detalle/Recotizar';
import { Tiempo } from './detalle/Tiempo';

export default function TrabajoDetalle() {
  const { id = '' } = useParams();
  const q = useTrabajo(id);
  const programa = usePrograma();
  const t = q.data;

  // Borrador de la solicitud: operaciones por capa, cantidades, material… hasta que se recotiza.
  const guardada = t ? JSON.stringify(t.solicitud) : '';
  const [borrador, setBorrador] = useState<SolicitudCotizacion | null>(null);
  useEffect(() => { if (t) setBorrador(structuredClone(t.solicitud)); }, [guardada]); // eslint-disable-line react-hooks/exhaustive-deps
  const cambios = Boolean(t && borrador && JSON.stringify(borrador) !== guardada);

  const programado = useMemo(() => programa.data?.trabajos.find((p) => p.trabajoId === id), [programa.data, id]);

  if (q.isLoading) return <Cargando />;
  if (q.isError || !t) return <><Link to="/" className="volver">← Trabajos</Link><ErrorCaja error={q.error} reintentar={() => q.refetch()} /></>;
  const solicitud = borrador ?? t.solicitud;
  const editable = puedeRecotizar(t.estado);
  const atraso = atrasoDe(t, programado);

  const setOperacion = (archivoId: string, capaId: string, op: Operacion) => setBorrador((b) => b && ({
    ...b, lineas: b.lineas.map((l) => (l.archivoId === archivoId ? { ...l, operaciones: { ...l.operaciones, [capaId]: op } } : l)),
  }));
  const setLinea = (archivoId: string, cambio: { cantidad?: number; grabarRasters?: boolean; escalaX?: number; escalaY?: number }) => setBorrador((b) => b && ({
    ...b, lineas: b.lineas.map((l) => (l.archivoId === archivoId ? { ...l, ...cambio } : l)),
  }));

  return (
    <>
      <Link to="/" className="volver">← Trabajos</Link>
      <div className="cab-trabajo">
        <h1>{t.cliente.nombre}</h1>
        <span className="folio">{t.folio}</span>
        <Chip estado={t.estado} />
        {t.urgente && <Insignia tono="accent">Urgente</Insignia>}
        {atraso && <Insignia tono="bad" title={atraso.titulo}>{atraso.texto}</Insignia>}
        <div className="grow" />
        <span className="celda-2 derecha">
          <b className="num" style={{ fontSize: '1.2rem' }}>{pesos(totalACobrar(t))}</b>
          <small>compromiso {fechaHora(t.fechaCompromiso)} · {relativoDias(t.fechaCompromiso)}</small>
        </span>
      </div>

      <div className="rejilla-detalle">
        <div className="pila">
          <Archivos trabajo={t} solicitud={solicitud} editable={editable} cambios={cambios} onOperacion={setOperacion} onLinea={setLinea} />
          <Cotizaciones trabajo={t} cambiosPendientes={cambios} />
          {editable && borrador && <Recotizar trabajo={t} borrador={borrador} setBorrador={setBorrador} cambios={cambios} onDeshacer={() => setBorrador(structuredClone(t.solicitud))} onLinea={setLinea} />}
        </div>
        <div className="pila col-lateral">
          <Gestion trabajo={t} programado={programado} />
          <Tiempo trabajo={t} />
          <Contacto trabajo={t} />
          <Notas trabajo={t} />
          <Eventos trabajoId={t.id} />
        </div>
      </div>
    </>
  );
}
